import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { createEventRule, updateEventRule, listEntityColRules, listEventMappings } from '../api/derivationConfig'
import type { EventRule, EventMapping, ImpactRef, ArrayImpactGroup } from '../types'

interface EventRuleModalProps {
  existing?: EventRule
  accId?: number
  onClose: () => void
}

interface ImpactForm {
  ref: string
  mode: string
  expr: string
}

interface DerivationForm {
  attr: string
  impacts: ImpactForm[]
}

interface ArrayImpactGroupForm {
  arrayAttr: string
  entityKey: string
  derivations: DerivationForm[]
}

const UPDATE_MODES = ['SET', 'SET_IF_NULL', 'INC', 'MAX', 'MIN']

function toDerivationForms(derivations: Record<string, ImpactRef[]>): DerivationForm[] {
  return Object.entries(derivations ?? {}).map(([attr, impacts]) => ({
    attr,
    impacts: (impacts ?? []).map(imp => ({
      ref: imp.ref ?? '',
      mode: imp.mode ?? 'SET',
      expr: imp.expr ?? '',
    })),
  }))
}

function toDerivationsRecord(forms: DerivationForm[]): Record<string, ImpactRef[]> {
  const result: Record<string, ImpactRef[]> = {}
  for (const form of forms) {
    if (!form.attr.trim()) continue
    result[form.attr.trim()] = form.impacts
      .filter(imp => imp.ref.trim())
      .map(imp => ({
        ref: imp.ref.trim(),
        ...(imp.mode && imp.mode !== 'SET' ? { mode: imp.mode } : {}),
        ...(imp.expr.trim() ? { expr: imp.expr.trim() } : {}),
      }))
  }
  return result
}

function toArrayImpactGroupForms(groups: ArrayImpactGroup[] | undefined): ArrayImpactGroupForm[] {
  return (groups ?? []).map(g => ({
    arrayAttr: g.arrayAttr ?? '',
    entityKey: g.entityKey ?? '',
    derivations: toDerivationForms(g.derivations),
  }))
}

function toArrayImpactGroups(forms: ArrayImpactGroupForm[]): ArrayImpactGroup[] {
  return forms
    .filter(g => g.arrayAttr.trim() && g.entityKey.trim())
    .map(g => ({
      arrayAttr: g.arrayAttr.trim(),
      entityKey: g.entityKey.trim(),
      derivations: toDerivationsRecord(g.derivations),
    }))
}

export function EventRuleModal({ existing, accId, onClose }: EventRuleModalProps) {
  const queryClient = useQueryClient()
  const isEdit = !!existing

  const [formAccId, setFormAccId] = useState<string>(
    existing?.accId != null ? String(existing.accId) : accId != null ? String(accId) : '',
  )

  const [eventType, setEventType] = useState(existing?.eventType ?? '')
  const [category, setCategory]   = useState(existing?.category ?? '')
  const [eventName, setEventName] = useState(existing?.eventName ?? '')
  const [entityKey, setEntityKey] = useState(existing?.entityKey ?? '')
  const [derivations, setDerivations] = useState<DerivationForm[]>(
    existing ? toDerivationForms(existing.derivations) : [],
  )
  const [arrayImpactGroups, setArrayImpactGroups] = useState<ArrayImpactGroupForm[]>(
    existing ? toArrayImpactGroupForms(existing.arrayImpacts) : [],
  )
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  // ── Remote data ───────────────────────────────────────────────────────────

  const { data: colRules = [] } = useQuery({
    queryKey: ['entity-col-rules'],
    queryFn: () => listEntityColRules(),
  })
  const refNames = [...new Set(colRules.map(r => r.name))].sort()

  const { data: eventMappings = [] } = useQuery({
    queryKey: ['event-mappings', formAccId],
    queryFn: () => listEventMappings(formAccId ? parseInt(formAccId, 10) : undefined),
  })

  // ── Derived from selected event ───────────────────────────────────────────

  const selectedMapping: EventMapping | undefined = eventMappings.find(
    m => m.eventName === eventName && m.category === category && m.eventType === eventType,
  )
  const eventFields = selectedMapping ? Object.keys(selectedMapping.mappings).sort() : []

  // ── Event selection (create mode) ─────────────────────────────────────────

  const eventOptions = eventMappings.map(m => ({
    label: `${m.eventName}  ·  ${m.category}  ·  ${m.eventType}`,
    value: `${m.eventType}||${m.category}||${m.eventName}`,
    mapping: m,
  }))

  function handleEventSelect(value: string) {
    const opt = eventOptions.find(o => o.value === value)
    if (!opt) return
    setEventType(opt.mapping.eventType)
    setCategory(opt.mapping.category)
    setEventName(opt.mapping.eventName)
    setEntityKey('')
    setDerivations([])
    setArrayImpactGroups([])
  }

  const selectedEventValue = eventType && category && eventName
    ? `${eventType}||${category}||${eventName}`
    : ''

  // ── Mutation ──────────────────────────────────────────────────────────────

  const mutation = useMutation({
    mutationFn: (data: EventRule) =>
      isEdit
        ? updateEventRule(existing!.accId, existing!.eventType, existing!.category, existing!.eventName, data)
        : createEventRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-rules'] })
      setFeedback({ type: 'success', message: isEdit ? 'Event rule updated.' : 'Event rule created.' })
      setTimeout(onClose, 800)
    },
    onError: (err: Error) => {
      setFeedback({ type: 'error', message: err.message })
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const parsed = formAccId.trim() ? parseInt(formAccId, 10) : null
    const groups = toArrayImpactGroups(arrayImpactGroups)
    mutation.mutate({
      accId: parsed,
      eventType: eventType.trim(),
      category: category.trim(),
      eventName: eventName.trim(),
      entityKey: entityKey.trim(),
      derivations: toDerivationsRecord(derivations),
      ...(groups.length > 0 ? { arrayImpacts: groups } : {}),
    })
  }

  // ── Derivation helpers ────────────────────────────────────────────────────

  function addDerivation() {
    setDerivations(prev => [...prev, { attr: '', impacts: [{ ref: '', mode: 'SET', expr: '' }] }])
  }

  function removeDerivation(di: number) {
    setDerivations(prev => prev.filter((_, i) => i !== di))
  }

  function setAttr(di: number, value: string) {
    setDerivations(prev => prev.map((d, i) => i === di ? { ...d, attr: value } : d))
  }

  function addImpact(di: number) {
    setDerivations(prev => prev.map((d, i) =>
      i === di ? { ...d, impacts: [...d.impacts, { ref: '', mode: 'SET', expr: '' }] } : d
    ))
  }

  function removeImpact(di: number, ii: number) {
    setDerivations(prev => prev.map((d, i) =>
      i === di ? { ...d, impacts: d.impacts.filter((_, j) => j !== ii) } : d
    ))
  }

  function setImpact(di: number, ii: number, field: keyof ImpactForm, value: string) {
    setDerivations(prev => prev.map((d, i) =>
      i === di
        ? { ...d, impacts: d.impacts.map((imp, j) => j === ii ? { ...imp, [field]: value } : imp) }
        : d
    ))
  }

  // ── Array impact group helpers ────────────────────────────────────────────

  function addArrayGroup() {
    setArrayImpactGroups(prev => [...prev, {
      arrayAttr: '',
      entityKey: '',
      derivations: [{ attr: '', impacts: [{ ref: '', mode: 'SET', expr: '' }] }],
    }])
  }

  function removeArrayGroup(gi: number) {
    setArrayImpactGroups(prev => prev.filter((_, i) => i !== gi))
  }

  function setGroupField(gi: number, field: 'arrayAttr' | 'entityKey', value: string) {
    setArrayImpactGroups(prev => prev.map((g, i) => i === gi ? { ...g, [field]: value } : g))
  }

  function addGroupDerivation(gi: number) {
    setArrayImpactGroups(prev => prev.map((g, i) =>
      i === gi
        ? { ...g, derivations: [...g.derivations, { attr: '', impacts: [{ ref: '', mode: 'SET', expr: '' }] }] }
        : g
    ))
  }

  function removeGroupDerivation(gi: number, di: number) {
    setArrayImpactGroups(prev => prev.map((g, i) =>
      i === gi ? { ...g, derivations: g.derivations.filter((_, j) => j !== di) } : g
    ))
  }

  function setGroupAttr(gi: number, di: number, value: string) {
    setArrayImpactGroups(prev => prev.map((g, i) =>
      i === gi
        ? { ...g, derivations: g.derivations.map((d, j) => j === di ? { ...d, attr: value } : d) }
        : g
    ))
  }

  function addGroupImpact(gi: number, di: number) {
    setArrayImpactGroups(prev => prev.map((g, i) =>
      i === gi
        ? {
            ...g,
            derivations: g.derivations.map((d, j) =>
              j === di ? { ...d, impacts: [...d.impacts, { ref: '', mode: 'SET', expr: '' }] } : d
            ),
          }
        : g
    ))
  }

  function removeGroupImpact(gi: number, di: number, ii: number) {
    setArrayImpactGroups(prev => prev.map((g, i) =>
      i === gi
        ? {
            ...g,
            derivations: g.derivations.map((d, j) =>
              j === di ? { ...d, impacts: d.impacts.filter((_, k) => k !== ii) } : d
            ),
          }
        : g
    ))
  }

  function setGroupImpact(gi: number, di: number, ii: number, field: keyof ImpactForm, value: string) {
    setArrayImpactGroups(prev => prev.map((g, i) =>
      i === gi
        ? {
            ...g,
            derivations: g.derivations.map((d, j) =>
              j === di
                ? { ...d, impacts: d.impacts.map((imp, k) => k === ii ? { ...imp, [field]: value } : imp) }
                : d
            ),
          }
        : g
    ))
  }

  // ── Shared derivation block renderer ─────────────────────────────────────

  function renderDerivationBlock(
    derivList: DerivationForm[],
    onAdd: () => void,
    onRemove: (di: number) => void,
    onSetAttr: (di: number, v: string) => void,
    onAddImpact: (di: number) => void,
    onRemoveImpact: (di: number, ii: number) => void,
    onSetImpact: (di: number, ii: number, f: keyof ImpactForm, v: string) => void,
    attrDatalistId: string,
  ) {
    return (
      <>
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-gray-700">Derivations</span>
          <Button type="button" size="sm" variant="outline" onClick={onAdd}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add Attribute
          </Button>
        </div>

        <datalist id={attrDatalistId}>
          {eventFields.map(f => <option key={f} value={f} />)}
        </datalist>

        {derivList.length === 0 && (
          <p className="text-sm text-gray-400 italic py-1">No derivations yet.</p>
        )}

        <div className="space-y-3">
          {derivList.map((deriv, di) => (
            <div key={di} className="border border-gray-200 rounded-lg p-4 bg-white">
              <div className="flex items-center gap-2 mb-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Attribute (from event mapping)
                  </label>
                  <Input
                    list={attrDatalistId}
                    placeholder="e.g. item_name, quantity"
                    value={deriv.attr}
                    onChange={e => onSetAttr(di, e.target.value)}
                    className="font-mono text-sm"
                    required
                  />
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(di)}
                  className="mt-5 text-gray-400 hover:text-red-500"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              <div className="space-y-2">
                <div className="grid grid-cols-[1fr_120px_1fr_32px] gap-2 text-xs font-medium text-gray-500 px-0.5">
                  <span>Template Ref</span>
                  <span>Mode</span>
                  <span>Expression (SpEL, optional)</span>
                  <span />
                </div>
                {deriv.impacts.map((imp, ii) => (
                  <div key={ii} className="grid grid-cols-[1fr_120px_1fr_32px] gap-2 items-center">
                    <Input
                      list="ref-names"
                      placeholder="e.g. txn_item.item_name"
                      value={imp.ref}
                      onChange={e => onSetImpact(di, ii, 'ref', e.target.value)}
                      className="font-mono text-sm"
                      required
                    />
                    <select
                      value={imp.mode}
                      onChange={e => onSetImpact(di, ii, 'mode', e.target.value)}
                      className="h-9 w-full rounded-md border border-gray-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    >
                      {UPDATE_MODES.map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                    <Input
                      placeholder="#item_price * #quantity"
                      value={imp.expr}
                      onChange={e => onSetImpact(di, ii, 'expr', e.target.value)}
                      className="text-sm font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => onRemoveImpact(di, ii)}
                      className="text-gray-300 hover:text-red-500"
                      disabled={deriv.impacts.length === 1}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => onAddImpact(di)}
                  className="text-xs text-primary hover:underline mt-1"
                >
                  + Add impact
                </button>
              </div>
            </div>
          ))}
        </div>
      </>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-8">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-base font-semibold text-gray-900">
            {isEdit ? 'Edit Event Rule' : 'New Event Rule'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 py-5 space-y-5">

            {/* Acc ID */}
            <div className="max-w-xs">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Acc ID <span className="text-gray-400 font-normal">(blank = global)</span>
              </label>
              <Input
                type="number"
                placeholder="e.g. 3"
                value={formAccId}
                onChange={e => setFormAccId(e.target.value)}
                disabled={isEdit}
              />
            </div>

            {/* Event selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Event</label>
              {isEdit ? (
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'Event Type', value: eventType },
                    { label: 'Category',   value: category },
                    { label: 'Event Name', value: eventName },
                  ].map(f => (
                    <div key={f.label}>
                      <p className="text-xs text-gray-500 mb-1">{f.label}</p>
                      <div className="h-9 px-3 flex items-center rounded-md border border-gray-200 bg-gray-50 text-sm text-gray-700 font-mono">
                        {f.value}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <select
                    value={selectedEventValue}
                    onChange={e => handleEventSelect(e.target.value)}
                    className="h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    required
                  >
                    <option value="">— select an event —</option>
                    {eventOptions.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  {selectedEventValue && (
                    <div className="mt-2 grid grid-cols-3 gap-3 text-xs text-gray-500">
                      <span>Type: <span className="font-mono text-gray-700">{eventType}</span></span>
                      <span>Category: <span className="font-mono text-gray-700">{category}</span></span>
                      <span>Name: <span className="font-mono text-gray-700">{eventName}</span></span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Entity key */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Entity Key{' '}
                <span className="text-gray-400 font-normal text-xs">
                  — attribute whose value identifies the entity document
                </span>
              </label>
              <datalist id="event-fields">
                {eventFields.map(f => <option key={f} value={f} />)}
              </datalist>
              <Input
                list="event-fields"
                placeholder="e.g. txn_id"
                value={entityKey}
                onChange={e => setEntityKey(e.target.value)}
                required
                className="max-w-xs font-mono"
              />
            </div>

            {/* Derivations */}
            <div>
              <datalist id="ref-names">
                {refNames.map(n => <option key={n} value={n} />)}
              </datalist>

              {renderDerivationBlock(
                derivations,
                addDerivation,
                removeDerivation,
                setAttr,
                addImpact,
                removeImpact,
                setImpact,
                'attr-names',
              )}
            </div>

            {/* Array Impacts */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Array Impacts</label>
                  <p className="text-xs text-gray-400">
                    One entity document per array element — e.g. line items in a transaction.
                  </p>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={addArrayGroup}>
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Add Group
                </Button>
              </div>

              {arrayImpactGroups.length === 0 && (
                <p className="text-sm text-gray-400 italic py-2">
                  No array impact groups — optional.
                </p>
              )}

              <div className="space-y-4 mt-3">
                {arrayImpactGroups.map((group, gi) => (
                  <div key={gi} className="border border-indigo-200 rounded-xl p-4 bg-indigo-50/30">
                    <div className="flex items-start justify-between mb-4">
                      <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">
                        Group {gi + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeArrayGroup(gi)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Array Attribute
                          <span className="text-gray-400 font-normal ml-1">— attribute holding the array</span>
                        </label>
                        <Input
                          list="event-fields"
                          placeholder="e.g. items"
                          value={group.arrayAttr}
                          onChange={e => setGroupField(gi, 'arrayAttr', e.target.value)}
                          className="font-mono text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Element Key
                          <span className="text-gray-400 font-normal ml-1">— field within each element</span>
                        </label>
                        <Input
                          placeholder="e.g. item_id"
                          value={group.entityKey}
                          onChange={e => setGroupField(gi, 'entityKey', e.target.value)}
                          className="font-mono text-sm"
                          required
                        />
                      </div>
                    </div>

                    {renderDerivationBlock(
                      group.derivations,
                      () => addGroupDerivation(gi),
                      (di) => removeGroupDerivation(gi, di),
                      (di, v) => setGroupAttr(gi, di, v),
                      (di) => addGroupImpact(gi, di),
                      (di, ii) => removeGroupImpact(gi, di, ii),
                      (di, ii, f, v) => setGroupImpact(gi, di, ii, f, v),
                      `group-${gi}-attr-names`,
                    )}
                  </div>
                ))}
              </div>
            </div>

            {feedback && (
              <div className={`text-sm px-4 py-2 rounded-lg ${
                feedback.type === 'success'
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {feedback.message}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Rule'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
